import { strIsNullOrEmpty } from '@fox/atoms';
import {
	CreateEventTicketReservationInputModel,
	CreateReservationInputModel,
	ExpireEventTicketReservationInputModel,
	ExtendReservationInputModel,
	ReservationUpdate,
	SwiftFoxEventCapacityStatusResult,
	SwiftFoxReservationRequestResult,
	SwiftFoxReservationResponse,
	SwiftFoxReservationResult,
	TicketDefinition,
	UpdateActiveReservationCountInputModel
} from './contracts';
import { addContentId, mutateHelper, queryHelper } from './fetch-templates';
export async function createReservation(input: {
	contentId: string;
	requestToken: string;
	eventId: string;
	count?: number;
}): Promise<SwiftFoxEventCapacityStatusResult | undefined> {
	const { contentId, requestToken, eventId, count = 1 } = input;
	const url = '/Umbraco/Api/Membership/CreateReservation';
	const res = await mutateHelper<SwiftFoxEventCapacityStatusResult, CreateReservationInputModel>(
		{ contentId, eventId, count },
		requestToken,
		url
	);

	if (res.error) {
		throw new Error(res.error);
	}

	return res.data;
}
export async function createTicketReservation(input: {
	contentId: string;
	requestToken: string;
	eventId: string;
	ticketId?: string;
	count?: number;
	reservationId?: string;
	enforceCount?: boolean;
}): Promise<SwiftFoxReservationRequestResult | undefined> {
	const { contentId, requestToken, eventId, ticketId, count, reservationId, enforceCount } = input;
	const url = '/Umbraco/Api/Membership/CreateTicketReservation';
	const res = await mutateHelper<SwiftFoxReservationRequestResult, CreateEventTicketReservationInputModel>(
		{ contentId, eventId, formRequestId: requestToken, reservationId, ticketId, count, enforceCount },
		requestToken,
		url
	);

	if (res.error) {
		throw new Error(res.error);
	}

	return res.data;
}
export async function expireTicketReservation(input: {
	contentId: string;
	requestToken: string;
	eventId: string;
	reservationId: string;
}): Promise<SwiftFoxReservationResult | undefined> {
	const { contentId, requestToken, eventId, reservationId } = input;
	const url = '/Umbraco/Api/Membership/ExpireTicketReservation';
	const res = await mutateHelper<SwiftFoxReservationResult, ExpireEventTicketReservationInputModel>(
		{ contentId, eventId, reservationId },
		requestToken,
		url
	);

	if (res.error) {
		throw new Error(res.error);
	}

	return res.data;
}
export async function extendReservation(input: {
	contentId: string;
	requestToken: string;
	reservationId: string;
	messageKey: string;
	safeExpiry?: boolean;
}): Promise<{
	success: boolean;
	reservation?: SwiftFoxReservationResult;
	message?: string;
}> {
	const { reservationId, messageKey, safeExpiry = false, contentId, requestToken } = input;
	if (strIsNullOrEmpty(reservationId)) return { success: false, message: 'No reservation ID provided' };

	const url = '/Umbraco/Api/Membership/ExtendReservation';
	const res = await mutateHelper<SwiftFoxReservationResponse, ExtendReservationInputModel>(
		{ contentId, reservationId, messageKey, safeExpiry },
		requestToken,
		url
	);

	if (res.error) {
		return { success: false, message: res.error };
	}

	if (res.data?.success) {
		return { success: true, reservation: res.data?.reservation ?? undefined };
	} else {
		return {
			success: false,
			message: res.data?.message ?? 'Could not extend reservation.'
		};
	}
}
export async function findReservationById(input: { contentId: string; requestToken: string; reservationId?: string }): Promise<{
	success: boolean;
	reservation?: SwiftFoxReservationResult;
	message?: string;
}> {
	const { reservationId, requestToken, contentId } = input;
	if (strIsNullOrEmpty(reservationId)) return { success: false, message: 'No reservation ID provided' };
	const url = `/Umbraco/Api/Membership/FindReservationById?${addContentId(contentId)}&id=${reservationId}`;
	const res = await queryHelper<SwiftFoxReservationResult>(requestToken, url);

	if (res.error) {
		return { success: false, message: res.error };
	}

	if (res.data) {
		return { success: true, reservation: res.data };
	} else {
		return { success: false, message: 'Unable to find reservation' };
	}
}

export async function fetchTicketDetails(input: {
	contentId: string;
	requestToken: string;
	eventId: string;
	allowedTickets: string[];
}): Promise<{ status: 'success' | 'error'; tickets?: TicketDefinition[]; availableQuotaRemaining?: number; message?: string }> {
	const { contentId, requestToken, eventId, allowedTickets } = input;
	let url = `/Umbraco/Api/Membership/GetTicketDetails?${addContentId(contentId)}&eventId=${eventId}`;

	if (allowedTickets?.length) {
		url += allowedTickets.map(allowedTicket => `&allowedTickets=${encodeURIComponent(allowedTicket)}`).join('');
	}
	const res = await queryHelper<{ status: 'success' | 'error'; tickets?: TicketDefinition[]; message?: string }>(requestToken, url);

	if (res.error) {
		return { status: 'error', message: res.error };
	}
	return res.data ?? { status: 'error', message: 'Unable to fetch ticket details.' };
}
export async function updateReservationCount(input: {
	contentId: string;
	requestToken: string;
	reservationId: string;
	operation: ReservationUpdate;
	count: number;
}): Promise<{ success: boolean; message?: string; reservationCount?: number }> {
	const { reservationId, operation, count, requestToken, contentId } = input;

	const url = '/Umbraco/Api/Membership/UpdateReservationCount';
	const res = await mutateHelper<SwiftFoxReservationResponse, UpdateActiveReservationCountInputModel>(
		{ contentId, reservationId, operation, count },
		requestToken,
		url
	);

	if (res.error) {
		return { success: false, message: res.error };
	}

	if (!res.data?.success) {
		return {
			success: false,
			message: res.data?.message ?? 'An error occured updating the reservation'
		};
	}
	return { success: true, reservationCount: res.data.reservation?.count };
}
export async function validateRsvpResponse(input: { contentId: string; requestToken: string; reservationId: string }): Promise<boolean> {
	const { reservationId, requestToken, contentId } = input;
	const url = `/Umbraco/Api/Membership/ValidateRSVPResponse?${addContentId(contentId)}&reservationId=${reservationId}`;
	const res = await queryHelper<boolean>(requestToken, url);

	if (res.error) {
		return false;
	}

	return res.data || false;
}
